import { isDevelopment, isProduction } from '@/config';

/**
 * Adaptive timeout configuration based on environment and endpoint type
 */

// Base timeouts by environment
const ENV_MULTIPLIER = isProduction ? 2.0 : isDevelopment ? 1.0 : 1.5;

// Default timeouts by operation type (in ms)
const DEFAULT_TIMEOUT = 6500;
const QUICK_TIMEOUT = 3000;
const STANDARD_TIMEOUT = 5000;
const EXTENDED_TIMEOUT = 12000;
const HEAVY_LIST_TIMEOUT = 12000; // Reduced from 20s - fail faster, rely on cached data
const SUPPORTING_DATA_TIMEOUT = 8000; // Reduced from 12s
// Long timeout for heavyweight operations (used for signup/onboarding flows)
// Reverted to a shorter default to avoid masking backend issues during diagnosis
const LONG_TIMEOUT = 20000; // 20,000 ms == 20 seconds
const EXPORT_TIMEOUT = 60000;

// Endpoint timeout rules - ordered by priority (first match wins)
// NOTE: These patterns match RTK Query endpoint names (not URL paths)
const TIMEOUT_RULES: Array<{ pattern: RegExp | string; timeout: number }> = [
  // Export operations (longest)
  { pattern: /export/i, timeout: EXPORT_TIMEOUT },

  // Team management operations (invite/cancel/resend)
  // Extend timeouts slightly to avoid client aborts racing backend success
  { pattern: 'inviteTeamMember', timeout: 12000 },
  { pattern: 'resendInvitation', timeout: 12000 },
  { pattern: 'deleteOrganizationMember', timeout: 12000 },

  // Onboarding signup (can involve Stytch + DB writes)
  { pattern: 'signupAgencyOwner', timeout: 45000 }, // 45s for signup (Stytch + DB operations can be slow)
  { pattern: /signup.*agency|agency.*signup/i, timeout: 45000 }, // 45s for any signup variations
  
  // Onboarding step operations
  { pattern: 'submitOnboardingStep', timeout: 45000 }, // 45s for onboarding atomic transactions
  { pattern: 'updateStepData', timeout: 15000 }, // 15s for saving step data
  { pattern: 'completeStep', timeout: 15000 }, // 15s for completing a step
  { pattern: /onboarding.*step/i, timeout: 30000 }, // 30s for other onboarding step operations

  // Organization members list can be moderately heavy after mutations
  { pattern: 'getOrganizationMembers', timeout: SUPPORTING_DATA_TIMEOUT },

  // External service operations (email, Stytch, etc.)
  { pattern: /complete-setup/i, timeout: LONG_TIMEOUT },

  // Compose: atomic create-conversation-and-send-message endpoints (DB writes + SMS delivery)
  { pattern: /getOrCreate.*Conversation.*WithMessage/i, timeout: 30000 }, // 30s base (60s prod) — atomic compose
  { pattern: /getOrCreate.*Conversation/i, timeout: 20000 }, // 20s base (40s prod) — conversation create/find

  // File uploads and messaging with attachments
  { pattern: /upload/i, timeout: 10000 },
  { pattern: 'sendMessage', timeout: 90000 }, // Allow 90s for messages with attachments and staging latency

  // Contact sync operations (external API calls to HawkSoft can be slow)
  { pattern: /syncExternalContacts|sync.*contacts/i, timeout: 30000 }, // 30s for external contact sync

  // Integration configuration (HawkSoft API validation can be slow)
  // Reduced from 150s to 60s - still generous but prevents excessive waiting
  { pattern: /configureHawksoft|configure.*integration/i, timeout: 60000 }, // 60s for integration configuration (reduced from 150s for better UX)
  { pattern: /integrations.*status|getIntegrationsStatus/i, timeout: 5000 }, // 5s for integration status check (quick operation)
  { pattern: /hawksoft|integration/i, timeout: 45000 }, // 45s for general integration operations (reduced from 90s)

  // Subscription operations (Stripe API calls can be slow)
  { pattern: 'createFreeSubscription', timeout: 60000 }, // 60s for free subscription (Stripe API can be very slow)
  { pattern: 'createSubscription', timeout: 60000 }, // 60s for paid subscription
  { pattern: /subscription/i, timeout: 45000 }, // 45s for other subscription operations
  { pattern: 'createPaymentSetup', timeout: 45000 }, // 45s for Stripe setup intent
  { pattern: /billing|payment|stripe/i, timeout: 30000 }, // 30s for other billing operations

  // Organization data fetches (generic) - increased to 15s for mutations that involve
  // cache invalidation, audit logging, and potential cold-start latency
  { pattern: /organization/i, timeout: 15000 },

  // Core dashboard data (initial render pulls these concurrently, so allow longer)
  { pattern: 'getInfiniteConversations', timeout: HEAVY_LIST_TIMEOUT },
  { pattern: 'getConversations', timeout: HEAVY_LIST_TIMEOUT },
  { pattern: 'getConversationById', timeout: HEAVY_LIST_TIMEOUT },
  { pattern: 'getMessagesForConversation', timeout: HEAVY_LIST_TIMEOUT },
  { pattern: 'getOrganizationTags', timeout: SUPPORTING_DATA_TIMEOUT },
  { pattern: /conversations/i, timeout: HEAVY_LIST_TIMEOUT },
  { pattern: /search/i, timeout: 6000 },

  // Auth operations (need to be responsive but allow for slow backends)
  { pattern: 'logout', timeout: QUICK_TIMEOUT }, // Logout should be fast - 3s
  { pattern: /login/i, timeout: STANDARD_TIMEOUT },
  { pattern: /refresh/i, timeout: STANDARD_TIMEOUT }, // 5s for token refresh
  { pattern: /validate|session/i, timeout: EXTENDED_TIMEOUT }, // 12s for session validation (critical for auth flow)
  { pattern: /auth.*signup|signup.*auth/i, timeout: 45000 }, // 45s for auth signup operations

  // Platform admin operations (heavy cleanup across many tables + external API calls)
  { pattern: 'deleteOrgUser', timeout: 60000 },
  { pattern: 'deleteOrganizationDetail', timeout: 60000 },
  { pattern: 'deleteOrgIntegration', timeout: 30000 },

  // Profile operations
  { pattern: /profile/i, timeout: STANDARD_TIMEOUT },

  // Quick operations (fallback)
  { pattern: /delete/i, timeout: QUICK_TIMEOUT },
  { pattern: /logs/i, timeout: STANDARD_TIMEOUT },
];

/**
 * Get timeout for a specific endpoint with environment adjustment
 */
export function getEndpointTimeout(endpoint: string | undefined): number {
  if (!endpoint) {
    return DEFAULT_TIMEOUT * ENV_MULTIPLIER;
  }
  
  // Find first matching rule
  for (const rule of TIMEOUT_RULES) {
    const matches = typeof rule.pattern === 'string' 
      ? endpoint.includes(rule.pattern)
      : rule.pattern.test(endpoint);
      
    if (matches) {
      const timeout = Math.round(rule.timeout * ENV_MULTIPLIER);
      return timeout;
    }
  }

  // Default timeout with environment multiplier
  return Math.round(DEFAULT_TIMEOUT * ENV_MULTIPLIER);
}

/**
 * Get retry configuration for an endpoint
 */
export function getRetryConfig(endpoint: string | undefined) {
  const isAuthEndpoint = endpoint?.includes('auth') || endpoint?.includes('login');
  const isCritical = isAuthEndpoint || endpoint?.includes('validate');
  
  return {
    maxRetries: isCritical ? 2 : 1,
    retryDelay: isCritical ? 500 : 1000,
    backoffMultiplier: 2,
    shouldRetry: (error: { status?: number | string }) => {
      const status = typeof error.status === 'number' ? error.status : 0;
      // Don't retry on client errors (4xx) except 408 (timeout) and 429 (rate limit)
      if (status >= 400 && status < 500) {
        return status === 408 || status === 429;
      }
      // Retry on network errors and server errors
      return true;
    }
  };
}